/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AccountDetailResponse } from '../models/AccountDetailResponse';
import type { AccountEditForm } from '../models/AccountEditForm';
import type { AccountIndexDownloadResponse } from '../models/AccountIndexDownloadResponse';
import type { AccountInitPasswordResponse } from '../models/AccountInitPasswordResponse';
import type { AccountRegisterForm } from '../models/AccountRegisterForm';
import type { AccountRegisterResponse } from '../models/AccountRegisterResponse';
import type { PageAccountIndexResponse } from '../models/PageAccountIndexResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class AccountRepository {

  /**
   * アカウント一覧表示
   * @returns PageAccountIndexResponse OK
   * @throws ApiError
   */
  public static index({
    id,
    loginId,
    statuses,
    lockStatuses,
    roles,
    email,
    acquirerNames,
    companyNames,
    shopNames,
    issuerNames,
    walletServiceNames,
    giftIssuerNames,
    page = 1,
    size = 20,
    sort,
  }: {
    /**
     * アカウントID
     */
    id: string | undefined,
    /**
     * ログインID
     */
    loginId: string | undefined,
    /**
     *
     * ステータス(複数指定可能)
     *
     * admin_userテーブルのdeleted_atに値があれば削除, なければ有効と判定する。
     * 削除されて2年以上経過しているユーザは検索対象外とする。
     *
     */
    statuses: Array<'AVAILABLE' | 'WITHDRAWAL'> | undefined,
    /**
     *
     * ロックステータス(複数指定可能)
     *
     * admin_userテーブルのlocked_atとlocked_nologin_atを元に判定をしている。
     * 本日日付がlocked_atよりも前の場合、ログインロック中
     * 本日日付がlocked_nologin_atよりも前の場合、未ログインロック中
     * 上記に該当しない場合、ロックなし
     *
     */
    lockStatuses: Array<'AVAILABLE' | 'LOCKED' | 'LOCKED_NO_LOGIN'> | undefined,
    /**
     * 権限(複数指定可能)
     */
    roles: Array<'SYSTEM_MANAGER' | 'SYSTEM_STAFF' | 'TENANT_MANAGER' | 'TENANT_STAFF' | 'ACQUIRER_MANAGER' | 'ACQUIRER_STAFF' | 'VALUE_ISSUER_MANAGER' | 'VALUE_ISSUER_STAFF' | 'WALLET_SERVICE_MANAGER' | 'WALLET_SERVICE_STAFF' | 'COMPANY_MANAGER' | 'COMPANY_STAFF' | 'SHOP_MANAGER' | 'SHOP_STAFF' | 'GIFT_ISSUER_MANAGER' | 'GIFT_ISSUER_STAFF'> | undefined,
    /**
     * メールアドレス
     */
    email: string | undefined,
    /**
     * アクワイアラ(複数指定可能)
     */
    acquirerNames: Array<string> | undefined,
    /**
     * 企業名(複数指定可能)
     */
    companyNames: Array<string> | undefined,
    /**
     * 店舗名(複数指定可能)
     */
    shopNames: Array<string> | undefined,
    /**
     * イシュア(複数指定可能)
     */
    issuerNames: Array<string> | undefined,
    /**
     * ウォレットサービス(複数指定可能)
     */
    walletServiceNames: Array<string> | undefined,
    /**
     * 商品券事業者(複数指定可能)
     */
    giftIssuerNames: Array<string> | undefined,
    /**
     * One-based page index (1..N)
     */
    page: number | undefined,
    /**
     * The size of the page to be returned
     */
    size: number | undefined,
    /**
     * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
     */
    sort: Array<string> | undefined,
  }): CancelablePromise<PageAccountIndexResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/account',
      query: {
        'id': id,
        'loginId': loginId,
        'statuses': statuses,
        'lockStatuses': lockStatuses,
        'roles': roles,
        'email': email,
        'acquirerNames': acquirerNames,
        'companyNames': companyNames,
        'shopNames': shopNames,
        'issuerNames': issuerNames,
        'walletServiceNames': walletServiceNames,
        'giftIssuerNames': giftIssuerNames,
        'page': page,
        'size': size,
        'sort': sort,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * アカウント登録
   * @returns AccountRegisterResponse OK
   * @throws ApiError
   */
  public static register({
    requestBody,
  }: {
    requestBody: AccountRegisterForm,
  }): CancelablePromise<AccountRegisterResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/account',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * アカウント一覧Csvを生成、ストレージにUploadし、ダウンロードするための署名付きURLを返却する
   * @returns AccountIndexDownloadResponse OK
   * @throws ApiError
   */
  public static downloadAccount({
    id,
    loginId,
    statuses,
    lockStatuses,
    roles,
    email,
    acquirerNames,
    companyNames,
    shopNames,
    issuerNames,
    walletServiceNames,
    giftIssuerNames,
  }: {
    /**
     * アカウントID
     */
    id: string | undefined,
    /**
     * ログインID
     */
    loginId: string | undefined,
    /**
     *
     * ステータス(複数指定可能)
     *
     * admin_userテーブルのdeleted_atに値があれば削除, なければ有効と判定する。
     * 削除されて2年以上経過しているユーザは検索対象外とする。
     *
     */
    statuses: Array<'AVAILABLE' | 'WITHDRAWAL'> | undefined,
    /**
     *
     * ロックステータス(複数指定可能)
     *
     * admin_userテーブルのlocked_atとlocked_nologin_atを元に判定をしている。
     * 本日日付がlocked_atよりも前の場合、ログインロック中
     * 本日日付がlocked_nologin_atよりも前の場合、未ログインロック中
     * 上記に該当しない場合、ロックなし
     *
     */
    lockStatuses: Array<'AVAILABLE' | 'LOCKED' | 'LOCKED_NO_LOGIN'> | undefined,
    /**
     * 権限(複数指定可能)
     */
    roles: Array<'SYSTEM_MANAGER' | 'SYSTEM_STAFF' | 'TENANT_MANAGER' | 'TENANT_STAFF' | 'ACQUIRER_MANAGER' | 'ACQUIRER_STAFF' | 'VALUE_ISSUER_MANAGER' | 'VALUE_ISSUER_STAFF' | 'WALLET_SERVICE_MANAGER' | 'WALLET_SERVICE_STAFF' | 'COMPANY_MANAGER' | 'COMPANY_STAFF' | 'SHOP_MANAGER' | 'SHOP_STAFF' | 'GIFT_ISSUER_MANAGER' | 'GIFT_ISSUER_STAFF'> | undefined,
    /**
     * メールアドレス
     */
    email: string | undefined,
    /**
     * アクワイアラ(複数指定可能)
     */
    acquirerNames: Array<string> | undefined,
    /**
     * 企業名(複数指定可能)
     */
    companyNames: Array<string> | undefined,
    /**
     * 店舗名(複数指定可能)
     */
    shopNames: Array<string> | undefined,
    /**
     * イシュア(複数指定可能)
     */
    issuerNames: Array<string> | undefined,
    /**
     * ウォレットサービス(複数指定可能)
     */
    walletServiceNames: Array<string> | undefined,
    /**
     * 商品券事業者(複数指定可能)
     */
    giftIssuerNames: Array<string> | undefined,
  }): CancelablePromise<AccountIndexDownloadResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/account/download',
      query: {
        'id': id,
        'loginId': loginId,
        'statuses': statuses,
        'lockStatuses': lockStatuses,
        'roles': roles,
        'email': email,
        'acquirerNames': acquirerNames,
        'companyNames': companyNames,
        'shopNames': shopNames,
        'issuerNames': issuerNames,
        'walletServiceNames': walletServiceNames,
        'giftIssuerNames': giftIssuerNames,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * アカウント削除
   * @returns any OK
   * @throws ApiError
   */
  public static delete({
    id,
  }: {
    id: string,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/account/{id}',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * アカウント詳細表示
   * @returns AccountDetailResponse OK
   * @throws ApiError
   */
  public static detail({
    id,
  }: {
    /**
     * アカウントID
     */
    id: string,
  }): CancelablePromise<AccountDetailResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/account/{id}',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * アカウント編集
   * @returns any OK
   * @throws ApiError
   */
  public static edit({
    id,
    requestBody,
  }: {
    id: string,
    requestBody: AccountEditForm,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/account/{id}',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * パスワード初期化
   * @returns AccountInitPasswordResponse OK
   * @throws ApiError
   */
  public static initPassword({
    id,
  }: {
    id: string,
  }): CancelablePromise<AccountInitPasswordResponse> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/account/{id}/init_password',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ログインロック解除
   * @returns any OK
   * @throws ApiError
   */
  public static unlock({
    id,
  }: {
    /**
     * アカウントID
     */
    id: string,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/account/{id}/unlock',
      path: {
        'id': id,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

}
